import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  LoaderIcon,
  Tooltip,
  Typography,
  useWindowWidth,
} from '@acceso/design-system';
import clsx from 'clsx';
import { createElement, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useSegmentFeaturedPeoplePermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentFeaturedPeoplePermissions';
import { useSegmentLogoPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentLogoPermissions';
import { useSegmentStatementsPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentStatementsPermissions';
import { useSegmentTextOnScreenPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentTextOnScreenPermissions';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { useClipFeaturedPeoplePermissions } from '@/features/clips/hooks/useClipFeaturedPeoplePermissions';
import { useClipLogosPermissions } from '@/features/clips/hooks/useClipLogosPermissions';
import { useClipPermissions } from '@/features/clips/hooks/useClipPermissions';
import { useClipStatementsPermissions } from '@/features/clips/hooks/useClipStatementsPermissions';
import { useClipTextOnScreenPermissions } from '@/features/clips/hooks/useClipTextOnScreenPermissions';
import { useClipTimeKeepingPermissions } from '@/features/clips/hooks/useClipTimeKeepingPermissions';
import { useSelectedLogos } from '@/features/logos/api/get-logos';
import { useLogosLabelsFilterStore } from '@/features/logos/stores/logos-labels-filter';
import { countLogosByLabels } from '@/features/logos/utils/count-logos-by-labels';

import {
  TOOLKIT,
  TOOLKIT_CLIP_RADIO,
  TOOLKIT_CLIPS,
  TOOLKIT_GLOBAL_VIEW,
  TOOLKIT_RADIO,
} from './constants';
import { ESegmentToolkit } from './types';

type TBroadcastScheduleProgramBodySegmentToolkitProps = {
  segments?: boolean;
  clips?: boolean;
};
export function BroadcastScheduleProgramBodySegmentToolkit({
  segments,
  clips,
}: TBroadcastScheduleProgramBodySegmentToolkitProps) {
  const {
    hoveredId,
    pinnedId,
    toolkitComponents,
    toolkitClipComponents,
    toolkitCardRegionRef,
    activeSegment,
    activeClip,
    formBox,
    handleTooltikClick,
    isGlobalView,
    globalViewSegments,
  } = useBroadcastScheduleProgram();
  const logosLabelsFilter = useLogosLabelsFilterStore(state => state.labels);
  const { width } = useWindowWidth();
  const { t } = useTranslation();

  const logoPresences = useMemo(() => {
    if (logosLabelsFilter.length === 0) return [];
    if (isGlobalView) return globalViewSegments.flatMap(seg => seg.segment_logos ?? []);
    return clips ? (activeClip?.clip_logos ?? []) : (activeSegment?.segment_logos ?? []);
  }, [logosLabelsFilter, isGlobalView, globalViewSegments, clips, activeClip, activeSegment]);

  const presentLogoIds = useMemo(
    () => [
      ...new Set(
        logoPresences.map(presence => presence.logo_id).filter((id): id is string => !!id)
      ),
    ],
    [logoPresences]
  );

  const { data: presentLogos } = useSelectedLogos(presentLogoIds);

  const logosById = useMemo(
    () => Object.fromEntries((presentLogos?.data ?? []).map(logo => [logo.id, logo])),
    [presentLogos]
  );

  const cardWith = useMemo(() => {
    if (toolkitCardRegionRef.current) {
      return toolkitCardRegionRef.current.clientWidth;
    }
    return 0;
  }, [toolkitCardRegionRef.current?.clientWidth]);

  const activeToolkitId = useMemo(() => {
    return hoveredId || pinnedId;
  }, [hoveredId, pinnedId]);

  const selectedToolkit = useMemo(() => {
    if (isGlobalView) return TOOLKIT_GLOBAL_VIEW;

    if (
      activeSegment?.media_type.includes(ChannelMediaTypeEnum.RADIO) ||
      activeClip?.media_type?.includes(ChannelMediaTypeEnum.RADIO)
    ) {
      return clips ? TOOLKIT_CLIP_RADIO : TOOLKIT_RADIO;
    }

    if (segments || clips) {
      return TOOLKIT_CLIPS;
    }

    return TOOLKIT;
  }, [isGlobalView, activeSegment?.media_type, activeClip?.media_type, segments, clips]);

  const { canSee: canSeeClips, canUpdate: canUpdateClips } = useClipPermissions();
  const { canSee: canSeeSegmentLogo } = useSegmentLogoPermissions();
  const { canSee: canSeeSegmentFeaturedPeople } = useSegmentFeaturedPeoplePermissions();
  const { canSee: canSeeSegmentStatements } = useSegmentStatementsPermissions();
  const { canSee: canSeeSegmentTextOnScreen } = useSegmentTextOnScreenPermissions();
  const { canSee: canSeeClipLogos } = useClipLogosPermissions();
  const { canSee: canSeeClipFeaturedPeople } = useClipFeaturedPeoplePermissions();
  const { canSee: canSeeClipStatements } = useClipStatementsPermissions();
  const { canSee: canSeeClipTextOnScreen } = useClipTextOnScreenPermissions();
  const { canSee: canSeeClipTimeKeeping } = useClipTimeKeepingPermissions();

  const filteredToolkit = selectedToolkit.filter(toolkit => {
    const isLogo = toolkit.value === ESegmentToolkit.LOGOTIPO;
    const isClip = toolkit.value === ESegmentToolkit.CORTES;
    const isFeaturedPeople = toolkit.value === ESegmentToolkit.PERSONAS;
    const isStatements = toolkit.value === ESegmentToolkit.DECLARACIONES;
    const isTextOnScreen = toolkit.value === ESegmentToolkit.ROTULOS;
    const isTimeKeeping = toolkit.value === ESegmentToolkit.TIMEKEEPING;

    if (segments) {
      if (isClip && !canSeeClips) return false;
      if (isLogo && !canSeeSegmentLogo) return false;
      if (isFeaturedPeople && !canSeeSegmentFeaturedPeople) return false;
      if (isStatements && !canSeeSegmentStatements) return false;
      if (isTextOnScreen && !canSeeSegmentTextOnScreen) return false;
    }

    if (clips) {
      if (isClip && !canUpdateClips) return false;
      if (isLogo && !canSeeClipLogos) return false;
      if (isFeaturedPeople && !canSeeClipFeaturedPeople) return false;
      if (isStatements && !canSeeClipStatements) return false;
      if (isTextOnScreen && !canSeeClipTextOnScreen) return false;
      if (isTimeKeeping && !canSeeClipTimeKeeping) return false;
    }

    return true;
  });

  const topX = useMemo(() => {
    if (isGlobalView) {
      return TOOLKIT_GLOBAL_VIEW.findIndex(toolkit => toolkit.value === activeToolkitId) * 72;
    }

    if (
      activeSegment?.media_type.includes(ChannelMediaTypeEnum.RADIO) ||
      activeClip?.media_type?.includes(ChannelMediaTypeEnum.RADIO)
    ) {
      return (
        (clips ? TOOLKIT_CLIP_RADIO : TOOLKIT_RADIO).findIndex(
          toolkit => toolkit.value === activeToolkitId
        ) * 72
      );
    }

    if (clips) {
      return TOOLKIT_CLIPS.findIndex(toolkit => toolkit.value === activeToolkitId) * 72;
    }

    return TOOLKIT.findIndex(toolkit => toolkit.value === activeToolkitId) * 72;
  }, [
    isGlobalView,
    activeToolkitId,
    clips,
    activeSegment?.media_type,
    activeClip?.media_type,
    segments,
  ]);

  const getToolkitCounter = (toolkitId: ESegmentToolkit) => {
    const globalLogosCount = isGlobalView
      ? globalViewSegments.reduce((acc, seg) => acc + (seg.segment_logos?.length ?? 0), 0)
      : 0;
    const globalPeoplesCount = isGlobalView
      ? globalViewSegments.reduce((acc, seg) => acc + (seg.segment_peoples?.length ?? 0), 0)
      : 0;
    const globalDeclarationsCount = isGlobalView
      ? globalViewSegments.reduce((acc, seg) => acc + (seg.segment_declarations?.length ?? 0), 0)
      : 0;

    const countMap: Partial<Record<ESegmentToolkit, number>> = {
      [ESegmentToolkit.TIMEKEEPING]: clips
        ? (activeClip?.clip_logos?.length ?? 0) + (activeClip?.clip_peoples?.length ?? 0)
        : 0,
      [ESegmentToolkit.LOGOTIPO]: isGlobalView
        ? globalLogosCount
        : clips
          ? (activeClip?.clip_logos?.length ?? 0)
          : (activeSegment?.segment_logos?.length ?? 0),
      [ESegmentToolkit.PERSONAS]: isGlobalView
        ? globalPeoplesCount
        : clips
          ? (activeClip?.clip_peoples?.length ?? 0)
          : (activeSegment?.segment_peoples?.length ?? 0),
      [ESegmentToolkit.DECLARACIONES]: isGlobalView
        ? globalDeclarationsCount
        : clips
          ? (activeClip?.clip_declarations?.length ?? 0)
          : (activeSegment?.segment_declarations?.length ?? 0),
    };

    const count = countMap[toolkitId] ?? 0;

    // Logos is the only toolkit whose badge reflects the active labels filter:
    // when a filter is applied it shows "shown / total" plus an explanatory tooltip.
    // A filtered count is only computed from the logos of the current ids; until they arrive,
    // or if their request fails, an empty map would report every presence as filtered out.
    const hasPresentLogosResolved = presentLogoIds.length === 0 || !!presentLogos;
    if (
      toolkitId === ESegmentToolkit.LOGOTIPO &&
      logosLabelsFilter.length > 0 &&
      hasPresentLogosResolved
    ) {
      const filteredCount = countLogosByLabels(logoPresences, logosLabelsFilter, logosById);

      return (
        <Tooltip
          help={t('Resultados mostrados / Resultados totales')}
          grow
          containerClasses="!absolute -top-0.5 right-2"
        >
          <Badge variant={BadgeVariantEnum.DefaultFilled}>
            {filteredCount} / {count}
          </Badge>
        </Tooltip>
      );
    }

    return (
      <Badge className="absolute -top-0.5 right-2" variant={BadgeVariantEnum.DefaultFilled}>
        {count}
      </Badge>
    );
  };

  return (
    <div
      className="relative z-20 order-4 flex max-h-[600px] flex-wrap gap-2 lg:max-h-full lg:flex-col lg:flex-nowrap"
      id="segments-toolkit"
    >
      {filteredToolkit.map(toolkit => {
        const isEditItem = toolkit.value === ESegmentToolkit.CORTES;
        const isEditDisabled = !!clips && !activeClip;
        const isDisabled = isEditItem && isEditDisabled;
        const markup = (
          <div
            key={toolkit.value}
            className={clsx(
              'relative flex flex-col items-center gap-1 py-2 hover:bg-white',
              !isDisabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
              pinnedId === toolkit.value && !isDisabled && 'bg-white'
            )}
            onClick={() => {
              if (!isDisabled) handleTooltikClick(toolkit.value);
            }}
          >
            {toolkit.countable && getToolkitCounter(toolkit.value)}
            {
              <toolkit.icon
                size={IconSizeEnum.XL}
                className={clsx(
                  (activeSegment?.review_state === 5 || activeClip?.review_state === 5) &&
                    toolkit.value === ESegmentToolkit.INFORMACION &&
                    '!text-feedback-error-20',
                  'text-accent-active-primary-30',
                  toolkit.value === ESegmentToolkit.CORTES &&
                    pinnedId !== ESegmentToolkit.CORTES &&
                    formBox === 'cut' &&
                    '!text-feedback-success-20',
                  isDisabled && 'opacity-40'
                )}
              />
            }
            <Typography
              fontSize={FontSizeEnum.Sm}
              weight={FontWeightEnum.Semibold}
              color={FontColorEnum.ActivePrimary}
              className="text-center"
            >
              {t(toolkit.label)}
            </Typography>
          </div>
        );

        if (toolkit.value === ESegmentToolkit.CORTES && !isGlobalView) {
          return <div className="border-b border-accent-active-primary-30 pb-4">{markup}</div>;
        }

        return markup;
      })}

      {width > 1024 && (
        <div
          className="absolute right-[70px] z-10 h-fit bg-white shadow-lg"
          style={{ width: cardWith ? `${cardWith}px` : '720px', top: `${topX}px` }}
        >
          {hoveredId && hoveredId !== pinnedId && (
            <Suspense fallback={<LoaderIcon className="animate infinite" />}>
              {clips
                ? createElement(toolkitClipComponents[hoveredId])
                : createElement(toolkitComponents[hoveredId])}
            </Suspense>
          )}
        </div>
      )}
    </div>
  );
}
